import {Component, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from "@angular/router";
import {NgxDatatableModule} from "@swimlane/ngx-datatable";
import {NgbDropdownModule, NgbModal, NgbTooltipModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {AdminWhatsappNewMessageModal} from "@cdss/pages/admin/messaging/modals/admin-whatsapp-new-message.modal";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import { AdminUsersService } from "@shared/api/services/v2/admin-users.service";
import {
    AdminUser,
    AdminDashboardFilters,
    ADMIN_DASHBOARD_DEFAULT_STATUSES,
    AdminUserEngagementEnum,
    AdminUserEngagementValue,
    getEngagementLabel,
    buildEngagementBadges,
    EngagementBadgeView
} from "@shared/api/models/query/admin-user.model";
import { Pagination, PaginationResponse } from "@models/pagination.model";
import { AdminDashboardFilterComponent } from "./filter/admin-dashboard-filter.component";
import { AdminDashboardFilterDto } from "./filter/admin-dashboard-filter.dto";
import { OverlaySpinnerComponent } from "@shared/components/overlay-spinner/overlay-spinner.component";
import { MaskedFieldComponent } from "@shared/components/masked-field/masked-field.component";
import { ConfirmDialogDirective } from "@shared/components/confirm-dialog/confirm-dialog.directive";
import { UserLicensesService } from "@shared/api/services/v2/user-licenses.service";
import { LicenseDeactivateModal } from "./modals/license-deactivate.modal";
import { LicenseHistoryModal } from "./modals/license-history.modal";
import { OtpModeStaticModal } from "./modals/otp-mode-static.modal";
import { OtpModeHistoryModal } from "./modals/otp-mode-history.modal";
import { OtpModeEnum } from "@shared/api/models/query/otp-mode-history.model";
import { OtpModeService } from "@shared/api/services/v2/otp-mode.service";

@Component({
    selector: 'cdss-admin-dashboard-page',
    imports: [
        CommonModule,
        RouterLink,
        NgxDatatableModule,
        NgbDropdownModule,
        NgbTooltipModule,
        SpinnerComponent,
        AdminDashboardFilterComponent,
        OverlaySpinnerComponent,
        MaskedFieldComponent,
        ConfirmDialogDirective
    ],
    standalone: true,
    templateUrl: './admin-dashboard.page.html',
    styleUrls: ['./admin-dashboard.page.scss']
})
export class AdminDashboardPage implements OnInit {
    loading = true;
    refreshing = false;
    @ViewChild("table") table: any;

    pagination: Pagination = new Pagination(25);
    rows: AdminUser[] = [];

    /**
     * UNIVAPLAT-2757 (review pass 3): `row.license === null` is ambiguous —
     * it means BOTH "deactivated" and "never licensed". The list payload
     * carries no discriminator (backend contract UNIVAPLAT-2756), so the
     * authoritative signal is the license-history endpoint: empty history
     * ⇒ never licensed; non-empty history + null active license ⇒ deactivated.
     *
     * This map caches that boolean per userId (true = has history, i.e.
     * deactivated). Absent key = unresolved (render neutral, never flash
     * "Deactivated"). Entries are invalidated after deactivate / reactivate /
     * edit-reason mutations so a refresh re-resolves the fresh state.
     */
    private licenseHistoryState = new Map<number, boolean>();

    filter: AdminDashboardFilterDto = new AdminDashboardFilterDto();
    showFilters = false;

    constructor(
        private breadcrumbService: BreadcrumbService,
        private toastr: ToastrService,
        private adminUsersService: AdminUsersService,
        private userLicensesService: UserLicensesService,
        private otpModeService: OtpModeService,
        private router: Router,
        private modalService: NgbModal,
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: 'Admin Dashboard', url: null}
        ]);

        this.refreshRows();
    }

    navigateToMessaging(userId: number, phoneNumber: string): void {
        this.router.navigate(['/cdss/admin/messaging', userId], { state: { maskedPhone: phoneNumber } });
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
    }

    onFilterApply(): void {
            this.refreshing = true;
            this.pagination.start = 0;
            this.refreshRows();
            this.showFilters = false;
    }

    onFilterCancel(): void {
        this.showFilters = false;
    }

    /**
     * UNIVAPLAT-2574 (Engagement column follow-up): resolves an engagement
     * wire value to its human-readable label via the model's
     * ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS-backed lookup — same source of
     * truth as the filter dropdown, so column and filter copy never drift.
     */
    engagementLabel(value: AdminUserEngagementValue): string {
        return getEngagementLabel(value);
    }

    /**
     * UNIVAPLAT-2574 (Engagement column UX follow-up, 2026-08-19): maps a
     * row's engagement buckets to the badges the column renders. Two or
     * more `week_<n>_not_completed` buckets collapse into a single
     * "<count> weeks not completed" badge whose per-week breakdown is
     * exposed via ngbTooltip; single buckets render unchanged with no
     * tooltip. Logic lives in the model (buildEngagementBadges) next to
     * the label lookup so column copy has one source of truth.
     */
    engagementBadges(engagement: AdminUserEngagementValue[]): EngagementBadgeView[] {
        return buildEngagementBadges(engagement);
    }

    /**
     * UNIVAPLAT-2574 (Engagement column follow-up): badge colour per
     * engagement bucket, mirroring the Status column's per-value badge
     * styling. Week buckets (`week_<n>_not_completed`) all share the
     * warning style — the label carries the week number.
     */
    engagementBadgeClass(value: AdminUserEngagementValue): string {
        switch (value) {
            case AdminUserEngagementEnum.NICELY_ENGAGED:
                return 'text-bg-success';
            case AdminUserEngagementEnum.NEVER_LOGGED_IN:
                return 'text-bg-danger';
            case AdminUserEngagementEnum.ONBOARDING:
                return 'text-bg-secondary';
            default:
                return 'text-bg-warning';
        }
    }

    /**
     * UNIVAPLAT-3142: makes the whole row clickable to expand/collapse the
     * row detail, not just the Patient cell's chevron. ngx-datatable's
     * (activate) fires for every cell click; skip clicks that originate on
     * a real interactive element (links, buttons, dropdown items/toggles)
     * so row expansion never hijacks the Action column's own click targets.
     */
    onRowActivate(event: any): void {
        if (event.type !== 'click') {
            return;
        }
        const target = event.event?.target as HTMLElement | undefined;
        if (target?.closest('a, button, [ngbdropdownitem], .dropdown-menu')) {
            return;
        }
        this.table.rowDetail.toggleExpandRow(event.row);
    }

    onSort(event: any): void {
        this.refreshing = true;
        this.pagination.sort = {prop: event.sorts[0].prop, order: event.sorts[0].dir};
        this.refreshRows();
    }

    setPaginationSize(size: number) {
        this.refreshing = true;
        this.pagination.size = size
        this.pagination.start = 0
        this.refreshRows()
    }

    previousPage() {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage - 2
        this.refreshRows()
    }

    nextPage() {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage
        this.refreshRows()
    }

    /**
     * UNIVAPLAT-2551: "Bulk actions" → "Send WhatsApp template" entry point.
     * Opens the compose modal in bulk mode with the CURRENT effective filter payload —
     * the exact same AdminDashboardFilters object the table query uses (buildFilters()),
     * so what the admin sees filtered is what the backend re-resolves recipients from.
     * The entry point is disabled in the template when pagination.totalCount === 0.
     */
    openBulkWhatsAppSend(): void {
        const ref = this.modalService.open(AdminWhatsappNewMessageModal, {size: 'lg', centered: true});
        ref.componentInstance.bulkMode = true;
        ref.componentInstance.bulkFilters = this.buildFilters();
        ref.componentInstance.bulkRecipientCount = this.pagination.totalCount;
        // Bulk sends do not mutate dashboard rows — nothing to refresh on close/dismiss.
        ref.result.catch(() => {/* dismissed */});
    }

    /**
     * UNIVAPLAT-2772: opens the license modal in `deactivate` mode for a row with
     * an ACTIVE license (row.license is the user's active license — null once
     * deactivated). No frontend permission gate beyond VIEW_ADMIN_DASHBOARD;
     * the backend enforces `updateAdmin` server-side.
     */
    openDeactivate(row: AdminUser): void {
        this.openLicenseModal(row, 'deactivate');
    }

    /**
     * UNIVAPLAT-2772: opens the license modal in `editReason` mode for a row
     * WITHOUT an active license. The modal itself resolves the most recent
     * deactivated license period (and its current reason) from the user's
     * license history — the dashboard row does not carry the period id.
     */
    openEditReason(row: AdminUser): void {
        this.openLicenseModal(row, 'editReason');
    }

    /**
     * UNIVAPLAT-2773: reactivates the user's most recently deactivated license.
     * Triggered by the shared ConfirmDialogDirective's `confirmedClick` (no new
     * modal). On success the table refreshes so the badge flips back to Active
     * and the row's actions swap. On error the backend's message is surfaced
     * verbatim (409 license_already_active / no_license_history) — the backend
     * owns the user-facing error text. No frontend permission gate beyond
     * VIEW_ADMIN_DASHBOARD, consistent with UNIVAPLAT-2772.
     */
    async reactivateLicense(row: AdminUser): Promise<void> {
        try {
            await this.userLicensesService.reactivate(row.id);
            this.toastr.success('License reactivated');
            // UNIVAPLAT-2757: the mutation changes the user's license state —
            // drop the cached history flag so the refresh re-resolves it.
            this.licenseHistoryState.delete(row.id);
            this.refreshing = true;
            this.refreshRows();
        } catch (error: any) {
            this.toastr.error(error?.error?.error);
        }
    }

    /**
     * UNIVAPLAT-2757 (review pass 3): true only when the row is genuinely
     * DEACTIVATED — null active license AND resolved non-empty history.
     * Never-licensed rows (empty history) and rows whose history has not
     * resolved yet return false, so they render the neutral presentation
     * (no status badge, no Reactivate / Edit reason menu items).
     */
    isDeactivated(row: AdminUser): boolean {
        return !row.license && this.licenseHistoryState.get(row.id) === true;
    }

    /**
     * UNIVAPLAT-2757 (review pass 3): resolves the deactivated-vs-never-licensed
     * state for the current page's null-license rows. Called once per rows load
     * (NOT per row per render): fetches listHistory in parallel for the subset
     * of rows that (a) have no active license and (b) are not already cached,
     * so paging back to an already-seen page does not refetch. A rejected
     * lookup is treated as never-licensed (neutral) — the HTTP interceptor
     * already surfaces the error; we just avoid mislabelling the row.
     */
    private resolveLicenseStates(rows: AdminUser[]): void {
        const unresolved = rows.filter(row => !row.license && !this.licenseHistoryState.has(row.id));
        if (!unresolved.length) {
            return;
        }
        Promise.all(unresolved.map(async (row) => {
            try {
                const history = await this.userLicensesService.listHistory(row.id);
                this.licenseHistoryState.set(row.id, history.length > 0);
            } catch {
                // Unknown state → neutral/never-licensed rather than "Deactivated".
                this.licenseHistoryState.set(row.id, false);
            }
        }));
    }

    /**
     * UNIVAPLAT-2774: opens the license history modal for a row — available on
     * EVERY row regardless of active/deactivated state. The modal loads the
     * history itself (listHistory) and, since the UNIVAPLAT-2757 review pass 1,
     * lets the admin edit any deactivated period's reason from the list; it
     * closes with `true` if any reason was edited, in which case the rows
     * refresh so they stay consistent. No frontend permission gate beyond
     * VIEW_ADMIN_DASHBOARD, consistent with UNIVAPLAT-2772/2773.
     */
    openHistory(row: AdminUser): void {
        const ref = this.modalService.open(LicenseHistoryModal, {size: 'lg', centered: true});
        ref.componentInstance.userId = row.id;
        ref.componentInstance.userName = `${row.firstName} ${row.lastName}`.trim();
        ref.result.then((changed: boolean) => {
            // close(true) = a reason was edited from the history list → refresh.
            if (changed) {
                this.refreshing = true;
                this.refreshRows();
            }
        }).catch(() => {/* dismissed */});
    }

    /**
     * UNIVAPLAT-2991: true only when the row's OTP mode is explicitly
     * `static`. A row whose `otpMode` is missing from the payload —
     * defensive, since the backend contract (UNIVAPLAT-2977) is still
     * rolling out — is treated as dynamic rather than static, per spec.
     */
    isOtpModeStatic(row: AdminUser): boolean {
        return row.otpMode === OtpModeEnum.STATIC;
    }

    /**
     * UNIVAPLAT-2992: opens the "Turn to static" modal for a row whose OTP
     * mode is dynamic (or missing). Mirrors {@link openLicenseModal}'s
     * `ref.result.then((changed) => ...)` pattern: `close(true)` means the
     * switch succeeded, so the row refreshes via {@link onOtpModeSwitched}.
     */
    openTurnToStatic(row: AdminUser): void {
        const ref = this.modalService.open(OtpModeStaticModal, {centered: true});
        ref.componentInstance.userId = row.id;
        ref.componentInstance.userName = `${row.firstName} ${row.lastName}`.trim();
        ref.result.then((changed: boolean) => {
            if (changed) {
                this.onOtpModeSwitched();
            }
        }).catch(() => {/* dismissed */});
    }

    /**
     * UNIVAPLAT-2993: turns a row's OTP mode from static back to dynamic.
     * Triggered by the shared ConfirmDialogDirective's `confirmedClick`
     * directly on the "Turn to dynamic" menu item — same no-bespoke-modal
     * pattern as {@link reactivateLicense} (the confirm dialog's title/message
     * name the action and the user; no separate modal component). On success
     * the row refreshes via {@link onOtpModeSwitched} so the menu flips back
     * to "Turn to static"; on failure the backend's message is surfaced
     * verbatim — the backend owns the user-facing error text, consistent
     * with {@link reactivateLicense} and {@link OtpModeStaticModal.submit}.
     */
    async turnToDynamic(row: AdminUser): Promise<void> {
        try {
            await this.otpModeService.setDynamic(row.id);
            this.toastr.success('OTP mode switched to dynamic', 'Success');
            this.onOtpModeSwitched();
        } catch (error: any) {
            this.toastr.error(error?.error?.error);
        }
    }

    /**
     * UNIVAPLAT-2994: opens the OTP mode history modal — available whenever
     * the OTP settings group is shown (UNIVAPLAT-2991). Read-only audit
     * trail ({@link OtpModeService.listHistory} via {@link OtpModeHistoryModal});
     * no mutation happens here, so unlike {@link openHistory} there is
     * nothing to refresh on close — the ref result is only caught to
     * swallow the dismiss rejection.
     */
    openOtpHistory(row: AdminUser): void {
        const ref = this.modalService.open(OtpModeHistoryModal, {size: 'lg', centered: true});
        ref.componentInstance.userId = row.id;
        ref.componentInstance.userName = `${row.firstName} ${row.lastName}`.trim();
        ref.result.catch(() => {/* dismissed */});
    }

    /**
     * UNIVAPLAT-2991: post-switch refresh for a successful OTP mode change.
     * Mirrors {@link reactivateLicense} — refetches the current page via
     * refreshRows() so the row's `otpMode` (and therefore which OTP
     * settings menu item renders) updates without a page reload. Called by
     * the Turn to static / Turn to dynamic modals (UNIVAPLAT-2992/2993) on
     * a successful switch.
     */
    onOtpModeSwitched(): void {
        this.refreshing = true;
        this.refreshRows();
    }

    private openLicenseModal(row: AdminUser, mode: 'deactivate' | 'editReason'): void {
        const ref = this.modalService.open(LicenseDeactivateModal, {centered: true});
        ref.componentInstance.mode = mode;
        ref.componentInstance.userId = row.id;
        ref.componentInstance.userName = `${row.firstName} ${row.lastName}`.trim();
        ref.result.then((changed: boolean) => {
            // close(true) = a mutation happened → refresh so the badge/actions update.
            if (changed) {
                // UNIVAPLAT-2757: a deactivate creates a history entry — drop the
                // cached flag so the refresh re-resolves the row's state.
                this.licenseHistoryState.delete(row.id);
                this.refreshing = true;
                this.refreshRows();
            }
        }).catch(() => {/* dismissed */});
    }

    /**
     * UNIVAPLAT-2551: effective filter payload shared by the table query (refreshRows)
     * and the bulk-send flow (openBulkWhatsAppSend). Extracted so the two can never drift.
     */
    private buildFilters(): AdminDashboardFilters {
        return {
            // UNIVAPLAT-2574: engagement filter — omitted from the request
            // when empty (service skips it) so clearing returns the
            // unfiltered list.
            engagement: this.filter.engagement.length ? this.filter.engagement : [],
            role: this.filter.role.length ? this.filter.role : [],
            license: this.filter.license.length ? this.filter.license : [],
            careProgramme: this.filter.careProgramme.length ? this.filter.careProgramme : [],
            category: this.filter.category.length ? this.filter.category : [],
            organisation: this.filter.organisation.length ? this.filter.organisation : [],
            service: this.filter.service.length ? this.filter.service : [],
            department: this.filter.department.length ? this.filter.department : [],
            // UNIVAPLAT-2357: draft users must be excluded by default. The
            // backend only applies derived-status filtering when a status
            // param is present, so always send an explicit non-draft status
            // list unless the admin has chosen a status filter in the UI.
            status: this.filter.status.length ? this.filter.status : ADMIN_DASHBOARD_DEFAULT_STATUSES,
            currentWeek: this.filter.currentWeek.length ? this.filter.currentWeek : [],
            includeDemo: this.filter.includeDemo,
            signupDateStart: this.filter.signupDateStart || '',
            signupDateEnd: this.filter.signupDateEnd || '',
            firstLoginDateStart: this.filter.firstLoginDateStart || '',
            firstLoginDateEnd: this.filter.firstLoginDateEnd || '',
            lastLoginDateStart: this.filter.lastLoginDateStart || '',
            lastLoginDateEnd: this.filter.lastLoginDateEnd || ''
        };
    }

    refreshRows(): void {
        const filters: AdminDashboardFilters = this.buildFilters();

        this.adminUsersService.listAdminUsers(this.pagination, filters).subscribe({
            next: (response: PaginationResponse<AdminUser>) => {
                this.rows = response?.data || [];
                // UNIVAPLAT-2757: resolve deactivated-vs-never-licensed for the
                // page's null-license rows (parallel, cached per userId).
                this.resolveLicenseStates(this.rows);
                this.pagination.totalCount = response?.data?.length ? response?.pagination?.totalCount : 0;
                this.pagination.fromRecord = response?.data?.length ? response.pagination.fromRecord : 0;
                this.pagination.toRecord = response?.data?.length ? response.pagination.toRecord : 0;
                this.pagination.currentPage = response?.data?.length ? response.pagination.currentPage : 0;
                this.pagination.totalPages = response?.data?.length ? response.pagination.totalPages : 0;
                this.loading = false;
                this.refreshing = false;
            },
            error: (error) => {
                this.toastr.error('Failed to load admin users');
                this.loading = false;
                this.refreshing = false;
            }
        });
    }
}
